import * as z from 'zod'

import raw from './finExperiences.json'

// The 8 top-level groups a FinCategory rolls up into. Colocated with
// FinExperience since every experience references one and nothing else
// in the layer needs an inhabited row shape — the enum is the whole
// public API of "fin groups". Icons live in the playground
// (`groupColors.ts`); the double-entry sign convention lives inline in
// `TimelineRecord.ts` where it's applied.
export const finGroupNameSchema = z.enum([
  'Assets',
  'Contributions',
  'Coverage',
  'Expenses',
  'Income',
  'Liabilities',
  'Taxes',
  'Technical',
])

export type FinGroupName = z.infer<typeof finGroupNameSchema>

// Named-constant identity object for engine code — `FinGroups.Assets`
// instead of the raw string literal. Derived from the schema so the two
// can't drift out of sync.
export const FinGroups = finGroupNameSchema.enum

// A fin experience is the mid-level classification between FinGroup
// and FinCategory: it groups a set of FinCategories under a common
// user-facing concept (e.g. "Retirement" under Assets, "Employment" under
// Income). Every FinCategory belongs to exactly one experience via
// `experienceKey`, and every experience belongs to exactly one group
// via `finGroup`.
//
// The shipped set is ~37 rows. Engine code branches on `key` for four
// specific values (`RealEstate` in AnnualTaxes + createFinancialRecord,
// plus a handful of others referenced by ExperienceKey); the remaining
// keys exist as classification but don't drive branching. The set is
// effectively fixed by product structure.

export const finExperienceKeySchema = z.enum([
  'AssetsBank',
  'AssetsEducation',
  'AssetsEquity',
  'AssetsInsurance',
  'AssetsInvestment',
  'AssetsMiscellaneous',
  'AssetsRetirement',
  'ContributionsEducation',
  'ContributionsGeneral',
  'ContributionsRetirement',
  'CoverageBenefits',
  'ExpensesEntrepreneurship',
  'ExpensesGeneral',
  'ExpensesInsurance',
  'ExpensesMiscellaneous',
  'ExpensesObligation',
  'ExpensesPlanned',
  'ExpensesProperty',
  'IncomeEmployment',
  'IncomeEntrepreneurship',
  'IncomeInvestment',
  'IncomeMiscellaneous',
  'IncomeProperty',
  'IncomeRetirement',
  'IncomeSpecial',
  'LiabilitiesCredit',
  'LiabilitiesLoc',
  'RealEstate',
  'TaxesBenefit',
  'TaxesEntrepreneurship',
  'TaxesFica',
  'TaxesIncome',
  'TaxesOther',
  'TaxesProperty',
  'TechnicalQuest',
  'TechnicalTransaction',
  'Transfer',
])

export type FinExperienceKey = z.infer<typeof finExperienceKeySchema>

export const finExperienceSchema = z.object({
  name: z.string(),
  icon: z.string(),
  finGroup: finGroupNameSchema,
  key: finExperienceKeySchema,
})

export type FinExperience = z.infer<typeof finExperienceSchema>

export const FIN_EXPERIENCES: readonly FinExperience[] = z
  .array(finExperienceSchema)
  .parse(raw)

// O(1) lookup by key. Every value in `FinExperienceKey` is present,
// so callers can index directly (`FIN_EXPERIENCE_BY_KEY.RealEstate`)
// with no undefined check needed. This is the only lookup shape
// experiences need now that categories reference them by key rather
// than by id ([ZL-987]).
export const FIN_EXPERIENCE_BY_KEY: Record<FinExperienceKey, FinExperience> =
  Object.fromEntries(FIN_EXPERIENCES.map(e => [e.key, e])) as Record<
    FinExperienceKey,
    FinExperience
  >

/**
 * Named constants for fin experience keys, aliased directly to the
 * schema's identity object. Every key in `FinExperienceKey` is
 * accessible as `ExperienceKey.<Key>` — no separate curated list to
 * maintain, no drift when new keys are added.
 */
export const ExperienceKey = finExperienceKeySchema.enum
